import { deepEqual } from '@/lib/utils'
import {
    AdminLandingPage,
    isProductTargetedButton,
    isValidCombinedAtcBuyNowButtonText,
    LP_BUTTON_TYPE,
} from '@storefronts/common-types'
import { useRouter } from 'next/router'
import React, { Dispatch, SetStateAction, useCallback, useState } from 'react'
import { LandingPageEditorOverrideContext } from './LandingPageEditorOverrideContext'
import { useLandingPageLinks } from './LandingPageLinksContext'
import { StagedContentContextProvider } from './StagedContentContext'
import { StagedContentHistoryContextProvider } from './StagedContentHistoryContext'
import { transformContentToComparable } from './utils/transformToComparable'
import {
    sectionsFromContent,
    syncStorefrontProductsFromCatalog,
} from './utils/storefrontProducts'
import { isValidUrl } from '@/lib/utils'

export { LandingPageEditorOverrideContext }

type ComparableAdminLandingPage = Omit<AdminLandingPage, 'name' | 'updatedAt'>

export const LandingPageEditorOverrideContextProvider: React.FC<{
    initialLandingPage: AdminLandingPage
    setInitialLandingPage: Dispatch<
        SetStateAction<AdminLandingPage | undefined>
    >
    children?: React.ReactNode
}> = ({ initialLandingPage, setInitialLandingPage, children }) => {
    const router = useRouter()
    const { allLandingPageLinks } = useLandingPageLinks()
    const [stagedLandingPage, setStagedLandingPage] =
        useState<AdminLandingPage>({ ...initialLandingPage })
    const [sessionRemovedProductHashes, setSessionRemovedProductHashes] =
        useState<Set<string>>(new Set())

    const { preview } = router.query

    const setIsPreviewMode = (newPreviewMode: boolean) => {
        const newQuery = { ...router.query }
        if (newPreviewMode) {
            newQuery.preview = 'true'
        } else {
            delete newQuery.preview
        }

        router.replace(
            {
                pathname: router.pathname,
                query: newQuery,
            },
            undefined,
            { shallow: true },
        )
    }

    const [prevInitialLandingPage, setPrevInitialLandingPage] =
        useState(initialLandingPage)
    if (initialLandingPage !== prevInitialLandingPage) {
        setPrevInitialLandingPage(initialLandingPage)
        setStagedLandingPage({ ...initialLandingPage })
        // A fresh initial (e.g. after save) is the new baseline; forget any
        // session-level removal tracking so stale entries don't linger.
        setSessionRemovedProductHashes(new Set())
    }

    const recordProductRemovals = useCallback((linkHashes: string[]) => {
        if (linkHashes.length === 0) return
        setSessionRemovedProductHashes((previous) => {
            let changed = false
            const next = new Set(previous)
            for (const hash of linkHashes) {
                if (!next.has(hash)) {
                    next.add(hash)
                    changed = true
                }
            }
            return changed ? next : previous
        })
    }, [])

    const recordProductAdditions = useCallback((linkHashes: string[]) => {
        if (linkHashes.length === 0) return
        setSessionRemovedProductHashes((previous) => {
            let changed = false
            const next = new Set(previous)
            for (const hash of linkHashes) {
                if (next.delete(hash)) changed = true
            }
            return changed ? next : previous
        })
    }, [])

    const transformToComparable = (
        landingPage: AdminLandingPage,
    ): ComparableAdminLandingPage => {
        const { name, updatedAt, ...comparableLandingPage } = landingPage
        if (comparableLandingPage.bodyContent?.tabs?.length) {
            comparableLandingPage.bodyContent = transformContentToComparable(
                comparableLandingPage.bodyContent,
            )
        }
        return comparableLandingPage
    }

    const isLandingPageChanged = () => {
        // Reconcile both sides against the catalog so legacy backfill
        // (products referenced by sections but missing from landingPageLinks)
        // is not treated as an unsaved change. Staged reconciliation excludes
        // explicit session removals so a product the merchant deleted stays
        // removed even when a section still references it.
        const initialReconciled = syncStorefrontProductsFromCatalog(
            initialLandingPage,
            sectionsFromContent(initialLandingPage.bodyContent),
            allLandingPageLinks,
        )
        const stagedReconciled = syncStorefrontProductsFromCatalog(
            stagedLandingPage,
            sectionsFromContent(stagedLandingPage.bodyContent),
            allLandingPageLinks,
            sessionRemovedProductHashes,
        )
        return !deepEqual(
            transformToComparable(initialReconciled),
            transformToComparable(stagedReconciled),
        )
    }

    const isDesktopRedirectOverrideValid = () => {
        if (
            stagedLandingPage.desktopRedirectEnabledOverride === null ||
            stagedLandingPage.desktopRedirectEnabledOverride === undefined
        ) {
            return true
        }
        if (!stagedLandingPage.desktopRedirectEnabledOverride) return true
        const url = stagedLandingPage.desktopRedirectUrlOverride?.trim() ?? ''
        if (!url) return false
        return isValidUrl(url)
    }

    const isButtonConfigValid = () => {
        const button = stagedLandingPage.button
        if (!button) return true
        if (button.type === LP_BUTTON_TYPE.NONE) return true
        if (button.type === LP_BUTTON_TYPE.ADD_TO_CART_AND_BUY_NOW) {
            return (
                isProductTargetedButton(button) &&
                !!button.linkHash &&
                isValidCombinedAtcBuyNowButtonText(button.buttonText)
            )
        }
        if (!button.buttonText.trim()) return false
        if (button.type === LP_BUTTON_TYPE.LINK) {
            const url = button.url?.trim()
            if (!url) return false
            return isValidUrl(url)
        }
        if (isProductTargetedButton(button)) {
            return !!button.linkHash
        }
        return true
    }

    if (!stagedLandingPage || !stagedLandingPage.bodyContent) return null

    return (
        <LandingPageEditorOverrideContext.Provider
            value={{
                stagedLandingPage: stagedLandingPage,
                setStagedLandingPage: setStagedLandingPage,
                initialLandingPage: initialLandingPage,
                setInitialLandingPage: setInitialLandingPage,
                sessionRemovedProductHashes,
                recordProductRemovals,
                recordProductAdditions,
                isLandingPageChanged,
                isButtonConfigValid,
                isDesktopRedirectOverrideValid,
                isPreviewMode: preview === 'true',
                setIsPreviewMode,
            }}
        >
            <StagedContentContextProvider
                initContent={stagedLandingPage.bodyContent}
            >
                <StagedContentHistoryContextProvider>
                    {children}
                </StagedContentHistoryContextProvider>
            </StagedContentContextProvider>
        </LandingPageEditorOverrideContext.Provider>
    )
}
